import { Form, Formik } from 'formik';
import yup from '../../common/validation';
import { IOrderExclusion } from '../../models/OrderExclusions.model';
import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Spinner2 } from '../shared/spinner2';
import TextField from '../shared/formikFields/TextField';
import { DatePicker } from '../shared/formikFields';
import { debounceEventHandler } from '../shared/submitFormUtility';
import OrderExclusionService from '../../services/orderExclusionService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';
import moment from 'moment';
import { toLocalDateTimeString } from '../../utils/GeneralUtils';

interface IOrderExclusionModalProps {
  customerId: number;
  show: boolean;
  exclusion: IOrderExclusion;
  onHide: (renderer: boolean) => void;
}

export default function OrderExclusionModal(
  props: IOrderExclusionModalProps
): JSX.Element {
  const isNew = !props.exclusion;
  const { show, onHide } = props;

  const [isLoading, setLoading] = useState(false);

  const newExclusion = () => {
    const newExclusion: IOrderExclusion = {
      customerId: props.customerId,
      date: new Date(),
      id: null,
      name: '',
    };
    return newExclusion;
  };

  const getSchema = () => {
    return yup.object({
      name: yup.string().required().label(locale.orderExclusions._name),
    });
  };

  const onSubmit = async (exclusion: IOrderExclusion, isNew: boolean) => {
    if (isNew) {
      try {
        setLoading(true);
        await OrderExclusionService.createExclusion(exclusion);
        Toastr.success(locale.orderExclusions._createSuccess);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    } else {
      try {
        setLoading(true);
        // needs it to avoid time zone differences.
        const updatedExclusion = _.cloneDeep(exclusion);
        updatedExclusion.date = toLocalDateTimeString(new Date(exclusion.date));
        await OrderExclusionService.updateExclusion(updatedExclusion);
        Toastr.success(locale.orderExclusions._editSuccess);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    }
  };

  const exclusion = props.exclusion ? props.exclusion : newExclusion();
  return (
    <>
      <Formik
        onSubmit={(exclusion: IOrderExclusion) => onSubmit(exclusion, isNew)}
        initialValues={{ ...exclusion }}
        validationSchema={getSchema}
      >
        {({ handleSubmit, values, isSubmitting }) => (
          <Modal
            show={show}
            onHide={() => onHide(false)}
            className="order-exclusion-dialog"
          >
            <Spinner2 show={isLoading} />
            <Modal.Header closeButton>
              <Modal.Title>
                {isNew
                  ? locale.orderExclusions._newModalHeader
                  : locale.orderExclusions._updateModalHeader}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <TextField
                  label={locale.orderExclusions._name}
                  name="name"
                  placeholder={locale.orderExclusions._namePlaceHolder}
                />
                <DatePicker
                  label={locale.orderExclusions._date}
                  name="date"
                  popupClass="date-picker-popup-above-modal"
                  isLabelTop={true}
                  className="order-exclusion-datepicker"
                  format={locale.general._fullDateFormat}
                />
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => onHide(false)}>
                {locale.orderExclusions._cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={debounceEventHandler(handleSubmit, 250)}
                disabled={!values.name?.trim() || isSubmitting}
              >
                {locale.orderExclusions._save}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  );
}
